import { request } from "../../utils/request";

export const updateUserInfoApi = (body: USER.UpdateUserInfoPayload) => {
  return request<USER.UpdateUserInfoResponse>("/user/info", {
    method: "PUT",
    body
  });
};

/**
 * 获取当前用户信息
 * @param id 用户ID（可选，如果不传则从globalStore获取当前用户ID）
 * @returns Promise<USER.GetUserInfoResponse> 返回用户信息
 */
export const getUserInfoApi = async (id?: number) => {
  // 如果没有传入id，尝试从globalStore获取当前用户ID
  let userId = id;
  if (!userId) {
    try {
      // 使用动态导入避免循环依赖
      const { globalStore } = await import('../../utils/globalStore');
      const userInfo = globalStore.getUserInfo();
      if (userInfo?.id) {
        userId = userInfo.id;
      }
    } catch (error) {
      console.error('获取用户ID失败:', error);
    }
  }
  
  if (!userId) {
    throw new Error('无法获取用户ID');
  }
  
  return request<USER.GetUserInfoResponse>("/system/user/get", {
    method: "GET",
    params: { id: userId },
  });
};

/**
 * 获取用户列表（分页）
 * @param deptId 部门ID（可选）
 * @param username 账号（可选）
 * @param nickname 昵称（可选）
 * @param mobile 电话号码（可选）
 * @param pageNo 页码，默认1
 * @param pageSize 每页数量，默认7
 * @returns Promise<USER.UserListResponse> 用户列表响应
 */
export const getAllUsersApi = (deptId?: number, username?: string, nickname?: string, mobile?: string, pageNo: number = 1, pageSize: number = 7) => {
  const params: any = {
    pageNo,
    pageSize,
  };
  if (deptId) {
    params.deptId = deptId;
  }
  if (username) {
    params.username = username;
  }
  if (nickname) {
    params.nickname = nickname;
  }
  if (mobile) {
    params.mobile = mobile;
  }
  return request<USER.UserListResponse>("/system/user/page", {
    method: "GET",
    params,
  });
};

/**
 * 获取角色列表（分页）
 * @param pageNo 页码，默认1
 * @param pageSize 每页数量，默认20
 * @param name 角色名称（可选）
 * @param code 角色标识（可选）
 * @returns Promise<USER.RoleListResponse> 角色列表响应
 */
export const getAllRolesApi = (
  pageNo: number = 1, 
  pageSize: number = 20,
  name?: string,
  code?: string
) => {
  const params: any = {
    pageNo,
    pageSize,
  };
  
  if (name) {
    params.name = name;
  }
  
  if (code) {
    params.code = code;
  }
  
  return request<USER.RoleListResponse>("/system/role/page", {
    method: "GET",
    params,
  });
};

/**
 * 更新角色信息
 * @param body 角色更新数据
 * @returns Promise<USER.UpdateRoleResponse> 更新响应
 */
export const updateRoleApi = (body: USER.UpdateRolePayload) => {
  return request<USER.UpdateRoleResponse>("/system/role/update", {
    method: "PUT",
    body,
  });
};

/**
 * 创建用户
 * @param body 用户创建数据
 * @returns Promise<USER.CreateUserResponse> 创建响应
 */
export const createUserApi = (body: USER.CreateUserPayload) => {
  return request<USER.CreateUserResponse>("/system/user/create", {
    method: "POST",
    body,
  });
};

/**
 * 获取用户信息
 * @param id 用户ID
 * @returns Promise<USER.GetUserInfoResponse> 用户信息响应
 */
export const getUserInfoByIdApi = (id: number) => {
  return request<USER.GetUserInfoResponse>("/system/user/get", {
    method: "GET",
    params: { id },
  });
};

/**
 * 更新用户信息
 * @param body 用户更新数据
 * @returns Promise<USER.UpdateUserResponse> 更新响应
 */
export const updateUserApi = (body: USER.UpdateUserPayload) => {
  return request<USER.UpdateUserResponse>("/system/user/update", {
    method: "PUT",
    body,
  });
};

/**
 * 删除用户
 * @param id 用户ID
 * @returns Promise<USER.DeleteUserResponse> 删除响应
 */
export const deleteUserApi = (id: number) => {
  return request<USER.DeleteUserResponse>("/system/user/delete", {
    method: "DELETE",
    params: { id },
  });
};

/**
 * 分配用户角色
 * @param body 用户角色分配数据
 * @returns Promise<USER.AssignUserRoleResponse> 分配响应
 */
export const assignUserRoleApi = (body: USER.AssignUserRolePayload) => {
  return request<USER.AssignUserRoleResponse>("/system/permission/assign-user-role", {
    method: "POST",
    body,
  });
};

/**
 * 更新用户状态
 * @param body 用户状态更新数据
 * @returns Promise<USER.UpdateUserStatusResponse> 更新响应
 */
export const updateUserStatusApi = (body: USER.UpdateUserStatusPayload) => {
  return request<USER.UpdateUserStatusResponse>("/system/user/update-status", {
    method: "PUT",
    body,
  });
};

/**
 * 修改密码
 * @param body 密码修改数据（包含旧密码和新密码）
 * @returns Promise<USER.UpdatePasswordResponse> 修改密码响应
 */
export const updatePasswordApi = (body: USER.UpdatePasswordPayload) => {
  return request<USER.UpdatePasswordResponse>("/system/user/profile/update-password", {
    method: "PUT",
    body,
  });
};

/**
 * 退出登录
 * @returns Promise<USER.LogoutResponse> 退出登录响应
 */
export const logoutApi = () => {
  return request<USER.LogoutResponse>("/system/auth/logout", {
    method: "POST",
  });
};

/**
 * 修改用户信息
 * @param body 用户信息修改数据（所有字段都要传，不填会被置为null）
 * @returns Promise<USER.UpdateProfileResponse> 修改用户信息响应
 */
export const updateProfileApi = (body: USER.UpdateProfilePayload) => {
  return request<USER.UpdateProfileResponse>("/system/user/profile/update", {
    method: "PUT",
    body,
  });
};

/**
 * 获取部门用户列表
 * @param id 部门编号
 * @returns Promise<USER.DeptUsersResponse> 返回部门用户列表
 */
export const getDeptUsersApi = (id: number) => {
  return request<USER.DeptUsersResponse>("/system/dept/users", {
    method: "GET",
    params: { id },
  });
};
